/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/moon.svg";
import * as React from "react";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/app-frontend/core/BaseComponent";
import {COLORS} from "@res/colors";
import dayjs from "../../../utils/dayjs";
import {ICONS} from "@res/icons";
import {Day, Hour} from "@intuitionrobotics/ts-common";
import {ScaleLinear} from "d3-scale";
import {Coordinates, D3ChartData, GraphsModule, Sizing} from "../../../_to_remove/GraphsModule";
import AxisY from "../../../_to_remove/AxisY";
import * as d3 from "d3";
import {hideTooltip} from "../../widget/appTooltip";
import {AxisX, borderBoxCoordinates} from "../../../_to_remove/AxisX";

export type Frames = {
    startFrame?: number
    endFrame?: number
    min?: number,
    max?: number
}

export type Props = {
    patientId: string,
    data: D3ChartData[],
    viewBox: number,
    timeframe: number,
    sizing: Sizing,
    validRanges?: { [key: string]: { min: number, max: number } | undefined },
    lines?: boolean,
    frames?: Frames
    borderBox?: boolean,
    onHover?: (id: string, date: number, e?: DOMRect, label?: string) => void
}

type State = {
    hover: {
        hovering: boolean,
        date: number
        id: string
    }
    currentEndDate: number
};

// const moon = __asset_0;

const frequency = Day;

export class SelfReportGraph
    extends BaseComponent<Props, State> {

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (this.props.patientId !== prevProps.patientId)
            this.setState({currentEndDate: GraphsModule.getEndDate()});
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            hover: {hovering: false, id: "", date: 0},
            currentEndDate: GraphsModule.getEndDate()
        };
    }

    private dateAlignWidth = 68;

    private minAndMax = () => {
        return GraphsModule.extent(this.props.data.flatMap(_lineData => _lineData.data), this.props.frames);
    };


    private dataPlot = (label: string, report: Coordinates[], reportColor: string, xScale: ScaleLinear<number, number, any>, yScale: ScaleLinear<number, number, any>, validRanges?: {
        min: number,
        max: number
    }) => {
        if (!report || report.length === 0)
            return;

        const circleWidth = 4;
        return report.map((d, i) => {

            if (!this.checkMinMaxRange(d.y))
                return null;

            const hoveringMoment = dayjs(this.state.hover.date);
            const isHoveringOver = hoveringMoment.isSame(dayjs(d.x), "day");
            return <svg key={d._id}>
                <foreignObject
                    width={(circleWidth * 3) + 5} height={Math.abs(this.yScale()(d.y) - this.yScale()(this.minAndMax().minY)) + circleWidth}
                    y={yScale(d.y + circleWidth)}
                    x={xScale(d.x) - ((circleWidth * 3) / 2) + this.dateAlignWidth / 2}
                    onMouseEnter={(e) => {
                        this.setState({hover: {hovering: true, id: d._id ? d._id : d.x.toString(), date: d.x}});
                        this.props.onHover?.(d._id ? d._id : d.x.toString(), d.x, e.currentTarget.getBoundingClientRect(), label);
                    }}
                    onMouseLeave={() => {
                        this.setState({hover: {hovering: false, id: d._id ? d._id : d.x.toString(), date: d.x}});
                        this.props.onHover?.("", d.x + 1);
                        hideTooltip();
                    }}>
                    <div style={{
                        backgroundColor: this.state.hover.hovering && isHoveringOver ? COLORS.white : COLORS.very_light_blue,
                        width: circleWidth * 3,
                        height: Math.abs(this.yScale()(d.y) - this.yScale()(this.minAndMax().minY)) + circleWidth,
                        borderRadius: "5px 5px 0 0",
                        boxShadow: this.state.hover.hovering && isHoveringOver ? `0 1px 8px 0 #ececec` : "0 0 0 0"
                    }}/>
                </foreignObject>
                <circle
                    r={circleWidth}
                    cx={xScale(d.x) + this.dateAlignWidth / 2}
                    cy={yScale(d.y)}
                    style={validRanges && (d.y < validRanges.min || d.y > validRanges.max) ?
                        {fill: reportColor, stroke: "red", strokeWidth: "3px", pointerEvents: "all"} :
                        {fill: reportColor, pointerEvents: "all"}}
                    onMouseEnter={(e) => {
                        this.setState({hover: {hovering: true, id: d._id ? d._id : d.x.toString(), date: d.x}},
                            () => this.props.onHover?.(d._id ? d._id : d.x.toString(), d.x));
                    }}
                    onMouseLeave={() => {
                        this.setState({hover: {hovering: false, id: d._id ? d._id : d.x.toString(), date: d.x}},
                            () => this.props.onHover?.("", d.x + 1));
                    }}
                />
            </svg>
        });

    };

    checkMinMaxRange = (point: number) => {
        if (!this.props.frames || this.props.frames.min === undefined || !this.props.frames.max)
            return true;
        return point <= this.props.frames.max && point >= this.props.frames.min;
    };

    private lines = (color: string, data: Coordinates[], xScale: ScaleLinear<number, number, any>, yScale: ScaleLinear<number, number, any>): React.ReactNode[] | undefined => {
        if (!data || data.length === 0)
            return;

        // Lama ??
        if (data.length <= 1)
            return;

        const lineArray = [];
        for (let i = 0; i < data.length; i++) {
            const data1 = data[i + 1];
            const data0 = data[i];
            if (!data0 || !data1)
                continue;

            // The +12h is a damn hack. Lets fix this soon (end date should be today at midnight, not noon)
            if (GraphsModule.checkRange(
                data0.x,
                this.state.currentEndDate + (12 * Hour),
                this.props.viewBox,
                frequency
            ) && GraphsModule.checkRange(data1.x,
                this.state.currentEndDate + (12 * Hour),
                this.props.viewBox,
                frequency))
                lineArray.push(<line key={i}
                                     x1={xScale(data0.x) + this.dateAlignWidth / 2}
                                     x2={xScale(data1.x) + this.dateAlignWidth / 2}
                                     y1={this.checkMinMaxRange(data0.y) ? yScale(data0.y) : yScale(this.minAndMax().minY)}
                                     y2={this.checkMinMaxRange(data1.y) ? yScale(data[i + 1].y) : yScale(this.minAndMax().minY)}
                                     strokeWidth={2} stroke={color}/>);
        }
        return lineArray;
    };

    margin = {
        top: 20,
        bottom: 40,
        left: 0,
        right: 0
    };

    width = () => this.props.sizing.width - this.margin.right - this.margin.left - 30;
    height = () => this.props.sizing.height - this.margin.top - this.margin.bottom;

    xScale = () => GraphsModule.scale(this.state.currentEndDate - (frequency * this.props.viewBox), this.state.currentEndDate + (0.5 * Day), 0, this.width());

    yScale = () => GraphsModule.yScale(this.minAndMax().minY,
        this.minAndMax().maxY,
        this.height());


    getAxesDates = () => {
        const axesNumbers = [];
        let x = this.state.currentEndDate;
        for (let i = 0; i < this.props.viewBox; ++i) {
            axesNumbers.unshift(x);
            x = x - frequency;
        }
        return axesNumbers;
    };

    getAxesDiv = (axesNumbers: number[]) => {
        return axesNumbers.map((time, i) => {
            const isToday = dayjs(time).isSame(dayjs(), "day");
            const axesLabel = GraphsModule.getAxesLabel(time);
            const dateArray: string = isToday ? "Today" : axesLabel;
            const hovered = this.state.hover.hovering && dayjs(this.state.hover.date).isSame(dayjs(time), 'day');
            return <div key={i}>
                <div style={{
                    fontSize: 11,
                    textAlign: "center",
                    fontWeight: hovered ? "bold" : "normal",
                    color: hovered ? COLORS.black : COLORS.neutral_text_dark_2
                }}>{dateArray}</div>
                {isToday && <div style={{width: "8px", height: "8px", borderRadius: "50%", background: "black", margin: "0 auto"}}/>}
            </div>
        });
    }

    hoverColumn = (x1: number, xScale: ScaleLinear<number, number, any>) => {
        const date = new Date(x1).setHours(0, 0, 0, 0);
        const width = this.width() / this.props.viewBox;
        // this.props.onHover && this.props.onHover(x1);
        return <svg>
            <defs>
                <linearGradient id="gradient" x2="0%" y1="100%" y2="0%">
                    <stop offset="0%" stopColor={"#fff9ec"}/>
                    <stop offset="100%" stopColor={COLORS.white}/>
                </linearGradient>
            </defs>
            <rect width={width} fill={"url(#gradient)"} height={this.height()} x={(xScale(date) + this.dateAlignWidth / 2)} y={0}
                  style={{strokeWidth: 5, opacity: 0.8}}/>
        </svg>;
    };


    getBorderBoxValues = (): borderBoxCoordinates[] => {
        const axesLabels = this.getAxesDates();
        const borderY: borderBoxCoordinates[] = [];
        const hoverDate = dayjs(this.state.hover.date).startOf("day").valueOf();
        axesLabels.forEach(_label => {
            const startOfLabelDay = dayjs(_label).startOf("day").valueOf();
            const isHoveringMoon = this.state.hover.hovering && (hoverDate === startOfLabelDay || hoverDate === startOfLabelDay - Day);
            const isHoveringSun = this.state.hover.hovering && hoverDate === startOfLabelDay;
            borderY.push(
                {
                    value: ICONS.icon_moon(isHoveringMoon ? COLORS.black : COLORS.grays_2, 20),
                    placement: _label - Day
                })
            borderY.push(
                {
                    value: ICONS.icon_sun(
                        isHoveringSun ? COLORS.black : COLORS.grays_2, 20),
                    placement: _label - (Day / 2)
                })
        });
        const labelAtMidnight = dayjs().startOf("day").add(1, "d").valueOf();
        borderY.push(
            {
                value: ICONS.icon_moon( this.state.hover.hovering && hoverDate === labelAtMidnight - Day ? COLORS.black : COLORS.grays_2, 20),
                placement: labelAtMidnight
            })
        return borderY;
    };

    render() {
        const yScale = this.yScale();
        const xScale = this.xScale();
        const range = frequency * this.props.viewBox;
        return <div>
            <svg
                width={this.props.sizing.width}
                height={this.props.sizing.height}
                style={{overflow: "visible"}}
                onMouseMove={(e) => {
                    const date = Math.floor(xScale.invert(d3.pointer(e)[0] - 28));
                    if (date <= this.state.currentEndDate) {
                        this.setState({hover: {hovering: true, date: date, id: this.state.hover.id}});
                        this.props.onHover?.(this.state.hover.id, date);
                    }
                }}
                onMouseLeave={() => this.setState({hover: {hovering: false, id: "", date: 0}})}
            >
                <g transform={`translate(${0},${this.margin.top})`}>
                    <AxisY yScale={yScale}
                           width={this.width()}
                           height={this.height()}
                    />
                    {<AxisX xScale={xScale}
                            height={this.height()}
                            width={this.width()}
                            tickValues={this.getAxesDiv(this.getAxesDates())}
                            borderBoxValues={this.props.borderBox ? {
                                values: this.getBorderBoxValues()
                            } : undefined}
                            frequency={frequency}
                            minValue={this.state.currentEndDate - range}
                            viewBox={this.props.viewBox}
                            axisLine={false}/>}
                    {this.state.hover.hovering && this.hoverColumn(this.state.hover.date, xScale)}
                    <g>
                        {this.props.data.map(_data => this.dataPlot(_data.label as string, _data.data, _data.color, xScale, yScale, this.props.validRanges?.[_data.label as string]))}
                    </g>
                    <g>
                        {this.props.lines && this.props.data.map(_data => this.lines(_data.color, _data.data, xScale, yScale))}
                    </g>
                </g>
            </svg>
            {this.props.borderBox && <div style={{height: "30px"}}/>}
        </div>;
    }
}


export default SelfReportGraph;
